import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../../lib/api';

const AUTOSAVE_MS = 2000;

/**
 * Shared draft machinery for the DOLE report editors (the generic table forms and the
 * SPRS tree). It owns loading, debounced autosave, single-flight saving, optimistic
 * concurrency, reset, and export — everything except how a given form lays out its cells.
 *
 * The saved draft holds deltas only (overrides / tombstones / manual rows). Platform
 * content comes fresh from the server on every load, so this hook never owns the source
 * of truth for it; it owns the staff's edits on top and keeps them safe to persist.
 *
 * `data` is whatever the server composed — {rows,...} for table forms, {nodes,...} for
 * SPRS. The caller mirrors it into its own display state and calls `commit(nextDeltas)`
 * after each edit.
 */
export function useReportDraft(reportType, period, authHeaders) {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const timer = useRef(null);
  const deltasRef = useRef(null);   // latest deltas — the debounce reads this at fire time
  const baseRef = useRef(null);     // server-canonical updated_at for the conflict check
  const savingRef = useRef(false);  // one PUT at a time
  const dirtyRef = useRef(false);   // an edit arrived mid-save; save again when it lands
  const busyRef = useRef(false);    // reset/revert running — editing suspended

  const setBusyBoth = (v) => { busyRef.current = v; setBusy(v); };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const headers = await authHeaders();
      const res = await api.get(`/api/peso/reports/forms/${reportType}`, { headers, params: { period } });
      setData(res.data.data);
      setDeltas(res.data.deltas);
      deltasRef.current = res.data.deltas;
      baseRef.current = res.data.meta.updated_at;
      setMeta(res.data.meta);
    } catch (e) {
      setLoadError(true);
      toast.error(e?.response?.data?.detail || t('peso.reportLoadFailed', 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  }, [reportType, period, authHeaders, t]);

  useEffect(() => {
    load();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [load]);

  const save = useCallback(async (nextDeltas) => {
    if (!nextDeltas) return;
    if (savingRef.current) { dirtyRef.current = true; return; }
    savingRef.current = true;
    setSaving(true);
    try {
      const headers = await authHeaders();
      // The conflict base comes from the ref, refreshed by every response. Reading it
      // from render state would make a queued save collide with our own previous one.
      const res = await api.put(`/api/peso/reports/forms/${reportType}`,
        { data: nextDeltas, base_updated_at: baseRef.current },
        { headers, params: { period } });
      baseRef.current = res.data.updated_at;
      setMeta((m) => ({ ...m, draft_exists: true, updated_at: res.data.updated_at }));
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warning(t('peso.reportConflict', 'Someone else saved this report — reloading their version.'));
        load();
      } else {
        toast.error(e?.response?.data?.detail || t('peso.reportSaveFailed', 'Failed to save draft'));
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (dirtyRef.current) { dirtyRef.current = false; save(deltasRef.current); }
    }
  }, [reportType, period, authHeaders, load, t]);

  // Debounced autosave. Caller passes the full next deltas document.
  const commit = useCallback((nextDeltas) => {
    if (busyRef.current) return;
    deltasRef.current = nextDeltas;
    setDeltas(nextDeltas);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => save(deltasRef.current), AUTOSAVE_MS);
  }, [save]);

  // Wait out any save on the wire (and its follow-up) before an op that reloads/deletes.
  const drainSaves = async () => {
    while (savingRef.current || dirtyRef.current) {
      await new Promise((r) => setTimeout(r, 120));
    }
  };

  // Apply a delta mutation synchronously (save then reload), used by revert so a late
  // autosave cannot undo it. `mutate(current)` returns the next deltas document.
  const applyAndReload = useCallback(async (mutate) => {
    if (busyRef.current) return;
    setBusyBoth(true);
    try {
      if (timer.current) clearTimeout(timer.current);
      await drainSaves();
      const current = deltasRef.current;
      if (!current) return;
      const next = mutate(current);
      deltasRef.current = next;
      setDeltas(next);
      await save(next);
      await load();
    } finally {
      setBusyBoth(false);
    }
  }, [save, load]);

  const reset = useCallback(async () => {
    if (busyRef.current) return;
    setBusyBoth(true);
    try {
      if (timer.current) clearTimeout(timer.current);
      dirtyRef.current = false;
      await drainSaves();
      deltasRef.current = null;
      const headers = await authHeaders();
      await api.post(`/api/peso/reports/forms/${reportType}/reset`, null, { headers, params: { period } });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('peso.reportResetFailed', 'Failed to reset report'));
    } finally {
      setBusyBoth(false);
    }
  }, [reportType, period, authHeaders, load, t]);

  const download = useCallback(async (format) => {
    try {
      const headers = await authHeaders();
      const res = await api.get(`/api/peso/reports/forms/${reportType}/export`, {
        headers, params: { period, format }, responseType: 'blob', timeout: 60000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PESO_${reportType}_${period}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('peso.reportExportFailed', 'Export failed'));
    }
  }, [reportType, period, authHeaders, t]);

  return {
    data, deltas, meta, loading, loadError, saving, busy, busyRef,
    load, commit, applyAndReload, reset, download,
  };
}
